import * as React from 'react';

import { IconButton } from '@mui/material';
import type { IconButtonProps } from '@mui/material';
import { MessageCircle } from 'lucide-react';

import { AURA_TOUR_ANCHORS } from '@modules/_shared/components/chat/auraTour';
import {
  readChatPanelClosedInThisTab,
  readStoredChatPanelOpen,
  resolveInitialChatPanelOpen,
  writeChatPanelPreference,
} from '@modules/_shared/components/chat/chatPanelDefaults';
import { AppDataContext } from '@modules/_shared/providers/AppDataProvider';
import { canAccessChat as computeCanAccessChat } from '@modules/_shared/utils/access';
import { useLocale } from '@shared/hooks/locale';

type ChatPanelContextValue = {
  open: boolean;
  toggle: () => void;
  messagesRef: { current: unknown[] };
  chatIdRef: { current: string | null };
  // Survives ChatPanelInner remount (panel close/open) so the current chat
  // keeps its model. Not written to localStorage — new chats use the admin
  // default, not the last model the user picked in another conversation.
  modelIdRef: { current: string | null };
};

const ChatPanelContext = React.createContext<ChatPanelContextValue>({
  open: false,
  toggle: () => {},
  messagesRef: { current: [] },
  chatIdRef: { current: null },
  modelIdRef: { current: null },
});

export const useChatPanel = () => React.useContext(ChatPanelContext);

const isMac =
  typeof navigator !== 'undefined' && /Macintosh/.test(navigator.userAgent);

export const ChatPanelStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const messagesRef = React.useRef<unknown[]>([]);
  const chatIdRef = React.useRef<string | null>(null);
  const modelIdRef = React.useRef<string | null>(null);
  // Nullable on purpose, mirroring `ChatToggleButton`: keeps the provider safe
  // to mount outside the AppDataProvider. Under it — which is where `Layout`
  // puts it — `me` has already resolved, so the initial state below reads the
  // real module list on the first render and the panel never flickers.
  const appData = React.useContext(AppDataContext);
  const canAccessChat = appData != null && computeCanAccessChat(appData.me);
  // Evaluated once, at mount: growing the window mid-session must not pop the
  // panel open under the user.
  const [openPreference, setOpen] = React.useState(() =>
    resolveInitialChatPanelOpen({
      viewportWidth: window.innerWidth,
      storedPreference: readStoredChatPanelOpen(),
      closedInThisTab: readChatPanelClosedInThisTab(),
      canAccessChat,
    }),
  );
  // A stored `true` outlives the module being taken away, and `ChatPanelLayout`
  // is not even rendered without access — so an ungated `open` would leave the
  // layout reserving space for a panel that does not exist.
  const open = canAccessChat && openPreference;

  const toggle = React.useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      writeChatPanelPreference(next);
      return next;
    });
  }, []);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);

  React.useEffect(() => {
    const intercom = (window as any).Intercom;
    if (typeof intercom === 'function') {
      intercom('update', { hide_default_launcher: open });
    }
  }, [open]);

  const contextValue = React.useMemo(
    () => ({ open, toggle, messagesRef, chatIdRef, modelIdRef }),
    [open, toggle],
  );

  return (
    <ChatPanelContext.Provider value={contextValue}>
      {children}
    </ChatPanelContext.Provider>
  );
};

export const ChatToggleButton = ({ sx }: { sx?: IconButtonProps['sx'] }) => {
  const { t } = useLocale();
  const { open, toggle } = useChatPanel();
  // Nullable on purpose: keeps the button safe to render anywhere,
  // even outside the AppDataProvider.
  const appData = React.useContext(AppDataContext);
  const canAccessChat = appData != null && computeCanAccessChat(appData.me);

  if (open || !canAccessChat) {
    return null;
  }

  return (
    <IconButton
      data-tour={AURA_TOUR_ANCHORS.openChat}
      size="small"
      color="inherit"
      onClick={toggle}
      title={isMac ? t('Open chat (⌘J)') : t('Open chat (Ctrl+J)')}
      sx={sx ?? { mr: 1, ml: -0.5 }}
    >
      <MessageCircle size={22} strokeWidth={1.8} />
    </IconButton>
  );
};
